import { CloseRoundLight as CloseIcon } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { TGroup } from 'src/entities/tab/TabEntity';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';
import { WidgetWithEditMode } from 'src/entities/widget/WidgetWithEditMode';
import { useService } from 'src/packages/di';

import { GroupSelect } from '../group-select/GroupSelect';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import styles from './WidgetHeader.module.scss';

type Props = {
  widgetName: string;
  wellName?: string | number | boolean | null;
  wellboreName?: string | null;
  groupSelectStore: GroupSelectStore;
  onWidgetDelete(): void;
  onWidgetChangeGroup(group: TGroup | null): void;
  showCloseButton?: boolean;
  draggable?: boolean;
  entity: WidgetEntity;
  renderAdditionalButtons?: () => ReactNode;
  progress?: number | null;
  sectionTitle?: string;
  widgetTitleClassName?: string;
  widgetNameClassName?: string;
  wellNameClassName?: string;
  widgetTextClassName?: string;
};

export const WidgetHeader = observer(function WidgetHeader({
  wellName,
  widgetName,
  wellboreName,
  groupSelectStore,
  showCloseButton = true,
  draggable = true,
  entity,
  onWidgetDelete,
  onWidgetChangeGroup,
  renderAdditionalButtons,
  sectionTitle,
  progress,
  widgetTitleClassName,
  widgetNameClassName,
  wellNameClassName,
  widgetTextClassName,
}: Props) {
  const { t } = useTranslation();
  const widgetStore = useService('widgetStoreService').getStore(entity);
  const otherSettings = useService('otherSettings');

  return (
    <div className={clsx(styles.header, sectionTitle && styles.header_withCenterBlock)}>
      <div className={clsx(styles.widgetTitle, widgetTitleClassName && widgetTitleClassName)}>
        <GroupSelect
          groupSelectStore={groupSelectStore}
          widgetStore={widgetStore}
          selectedGroup={groupSelectStore.groupManager.groups.find((group) => group.id === entity.groupId)}
          onWidgetChangeGroup={onWidgetChangeGroup}
          isEditMode={entity instanceof WidgetWithEditMode && entity.isEditMode}
        />
        <div
          className={clsx(
            styles.draggableHandle,
            draggable && styles.draggableHandleDraggable,
            draggable && 'drag-handle',
          )}
        >
          <div
            className={clsx(styles.widgetName, widgetNameClassName && widgetNameClassName)}
            style={{ fontSize: otherSettings.widgetHeaderFontSize }}
          >
            {wellName != null && (
              <span className={clsx(styles.wellName, wellNameClassName && wellNameClassName)}>{`${wellName}${
                wellboreName != null ? ' /' : ''
              }`}</span>
            )}
            {wellboreName != null && (
              <span className={clsx(styles.wellName, wellNameClassName && wellNameClassName)}>{wellboreName}</span>
            )}
            {wellName != null ? ': ' : ''}
            <span>{`${widgetName}`}</span>
          </div>
        </div>
      </div>

      {sectionTitle && (
        <div
          className={clsx(
            styles.widgetSection,
            styles.draggableHandle,
            draggable && styles.draggableHandleDraggable,
            draggable && 'drag-handle',
          )}
        >
          <span className={styles.sectionName}>{sectionTitle}</span>
        </div>
      )}

      <div className={styles.controls}>
        <div
          className={clsx(
            styles.draggableHandle,
            draggable && styles.draggableHandleDraggable,
            draggable && 'drag-handle',
          )}
        ></div>
        {progress != null && <div className={styles.progressText}>{progress}%</div>}
        {renderAdditionalButtons?.()}
        {showCloseButton && (
          <Tooltip title={t('common:Widget.close')} delay={400}>
            <Button
              variant="flat"
              onClick={onWidgetDelete}
              icon={<CloseIcon className={styles.controlsButtonIcon} />}
              className={styles.controlsButton}
            />
          </Tooltip>
        )}
      </div>
      <div className={clsx(styles.progressBarContainer, !progress && styles.progressBarContainer__hidden)}>
        <div className={styles.progressBar} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
});
